import { randomBytes } from "crypto";

import { SurveyLimits } from "@teams/cards";
import {
  NominalQuestion,
  Questionnaire,
  TeamsApplicationTypes,
  TeamsScheduledReference,
  TeamsSurvey,
  TeamsSurveyAnonymity,
  TeamsSurveyComments,
  TeamsSurveyDeliveryPreference,
  TeamsSurveyDuration,
  TeamsSurveyQnaSettings,
  TeamsSurveyQuestionDurations,
  TeamsSurveyRecipients,
  TeamsSurveyResultsSettings,
  TeamsSurveyType,
  TeamsUser,
} from "@teams/types";
import moment from "moment";

const InstanceIdentifiers = {
  survey: () => `sur:fake_${randomBytes(16).toString("hex")}`,
  question: () => `squ:fake_${randomBytes(16).toString("hex")}`,
  choice: () => `cho:fake_${randomBytes(16).toString("hex")}`,
};

// copied from packages/core/src/surveys/CreateSurvey.ts

type CreateSurveyDetails = Questionnaire &
  TeamsSurveyAnonymity &
  TeamsSurveyDuration &
  TeamsSurveyComments &
  TeamsSurveyResultsSettings &
  TeamsSurveyType &
  TeamsSurveyRecipients &
  TeamsScheduledReference &
  TeamsSurveyDeliveryPreference &
  TeamsSurveyQnaSettings &
  TeamsSurveyQuestionDurations &
  Pick<
    TeamsSurvey,
    "reminders" | "openDuration" | "subscriptionId" | "templateId" | "duplicateFromSurveyId"
  >;

export const surveyDetailsToSurvey = (
  details: CreateSurveyDetails,
  owners: TeamsUser[],
  tenantId: string,
  applicationType: TeamsApplicationTypes
): Readonly<TeamsSurvey> => {
  const survey: TeamsSurvey = {
    id: InstanceIdentifiers.survey(),
    tenantId,
    applicationType,
    type: details.type,
    state: "pending",
    scheduleId: details.scheduleId,
    title: details.title,
    description: details.description,
    anonymity: details.anonymity,
    deliverAt: details.deliverAt ? moment(details.deliverAt).toDate() : undefined,
    closeAt: details.closeAt ? moment(details.closeAt).toDate() : undefined,
    commentsEnabled: details.commentsEnabled,
    commentsAppeal: details.commentsAppeal,
    resultsVisibility: details.resultsVisibility,
    resultsCloseNotification: details.resultsCloseNotification === true,
    responseCount: 0,
    createdAt: new Date(),
    updatedAt: new Date(),
    owners,
    recipients: details.recipients,
    questions: details.questions.map(q => {
      q.id = InstanceIdentifiers.question();
      if ((q as NominalQuestion).choices) {
        const nq = q as NominalQuestion;
        nq.choices.forEach(c => {
          c.id = InstanceIdentifiers.choice();
        });
      }
      return q;
    }),
    reminders: details.reminders,
    subscriptionId: details.subscriptionId,
    deliveryPreference: details.deliveryPreference,
    questionOpenDuration: details.questionOpenDuration,
    requireApproval: details.requireApproval,
    allowReactions: details.allowReactions,
    templateId: details.templateId,
    openDuration: details.openDuration,
    // only add if truthy value to avoid nulls in db
    ...(details.duplicateFromSurveyId && {
      duplicateFromSurveyId: details.duplicateFromSurveyId,
    }),
  };

  // @ts-ignore
  if (survey.deliverAt < new Date()) {
    survey.deliverAt = new Date();
  }

  if (survey.deliverAt) {
    const minDeliveryDate = new Date();
    if (survey.deliverAt < minDeliveryDate) {
      survey.deliverAt = minDeliveryDate;
    }
  }

  if (survey.closeAt) {
    const minCloseDate = moment(survey.deliverAt)
      .add(SurveyLimits.CloseAt.MinDurationFromDelivery)
      .toDate();
    if (survey.closeAt < minCloseDate) {
      survey.closeAt = minCloseDate;
    }
  }

  return survey;
};
